/**
 * On-Device Reminders — flat form ↔ API (nested §5) mappers (FEAT-REMINDERS-001).
 * The editor works in the prototype's flat `f` shape; the backend stores/validates
 * the nested rule. blankRule() seeds a new rule per type.
 */
import { blankGoalForm, goalFormFromApi, goalToApi } from '../../components/goal/goalModel';

// Flat form type 'profile' ↔ API type 'profile_date'.
const flatType = (apiType) => (apiType === 'profile_date' ? 'profile' : apiType);
const apiType = (flat) => (flat === 'profile' ? 'profile_date' : flat);

export function blankRule(type) {
  type = type || 'event';
  return {
    id: null, name: '', status: 'draft', type, audMode: 'all', segments: [], tags: [],
    title: '', body: '', tap: 'home', deeplink: '', vars: {},
    quiet: type === 'inactivity' || type === 'first_open', qStart: '22:00', qEnd: '08:00', maxPerDay: '',
    priority: type === 'prayer' ? 'high' : type === 'inactivity' ? 'low' : 'normal',
    cancelEvents: [], endDate: '',
    event: '', keyField: '', dateField: '', versionField: '', mode: 'before', amount: '24', unit: 'hours', days: '1', time: '09:00', late: 'skip', sample: '{}',
    conditions: [], // D7-COND (DA-BUG-1422): per-event property conditions (event + milestone·event)
    repeat: 'daily', times: ['09:00'], weekdays: [4], monthDay: '5', startDate: '2026-10-26',
    pMode: 'prayers', prayers: ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'], pWhen: 'before', pOffset: '10', method: 'karachi', shafaq: 'general', asr: 'hanafi', locSource: 'phone', highLat: 'middle', islamic: ['jumuah'], hijriAdj: '0',
    inMode: 'days', inAmount: '7', inUnit: 'days', byTime: '20:00', inRepeat: 'once', inEvery: '7', inMax: '3',
    foDays: '3', foTime: '10:00', foAfter: '',
    mCount: 'opens', mEvent: '', mRepeat: 'once', mN: '3', mDelay: '1', mDelayUnit: 'hours',
    attr: 'birthday', pdMode: 'on', pdAmount: '3', pdTime: '09:00', yearly: true,
    goal: blankGoalForm(),
  };
}

function varsToApi(vars) {
  const out = {};
  for (const [name, m] of Object.entries(vars || {})) {
    if (!m || !m.key) continue;
    const src = m.key[0] === 'e' ? 'event' : m.key[0] === 'c' ? 'contact' : 'builtin';
    out[name] = { source: src, key: m.key.slice(2), ...(m.fmt ? { format: m.fmt } : {}) };
  }
  return out;
}
function varsToForm(variables) {
  const out = {};
  for (const [name, m] of Object.entries(variables || {})) {
    const p = m.source === 'event' ? 'e' : m.source === 'contact' ? 'c' : 'b';
    out[name] = { key: `${p}:${m.key}`, fmt: m.format || '' };
  }
  return out;
}

// Shafaq (isha twilight) exists only for the Moonsighting Committee method (RRS reminders-sdk-payload.md;
// DA-BUG-0745). Other methods never carry it.
const SHAFAQ = ['general', 'ahmer', 'abyad'];
const shafaqFor = (f) => (f.method === 'moonsighting_committee' ? { shafaq: SHAFAQ.includes(f.shafaq) ? f.shafaq : 'general' } : {});
function prayerTiming(f) {
  return f.pMode === 'islamic'
        ? { mode: 'islamic_dates', islamicDates: f.islamic, method: f.method, asr: f.asr, location: f.locSource === 'phone' ? 'device' : 'profile_city', highLatitudeRule: 'middle_of_night', hijriAdjustDays: Number(f.hijriAdj) }
        : { mode: 'prayers', prayers: f.prayers, offsetMinutes: f.pWhen === 'before' ? Number(f.pOffset) : 0, method: f.method, asr: f.asr, location: f.locSource === 'phone' ? 'device' : 'profile_city', highLatitudeRule: 'middle_of_night', adjustmentsMinutes: { fajr: 0, dhuhr: 0, asr: 0, maghrib: 0, isha: 0 }, islamicDates: [], hijriAdjustDays: Number(f.hijriAdj) };
}

// D7-COND (DA-BUG-1422): clean the flat form's conditions → { conditions:[...] } or {} when none.
// Drops empty rows; `is_set`/`is_not_set` carry no value. Backend re-validates + normalizes.
const COND_VALUELESS = ['is_set', 'is_not_set'];
function condsApi(conds) {
  const out = (Array.isArray(conds) ? conds : [])
    .filter((c) => c && (c.property || '').trim())
    .map((c) => (COND_VALUELESS.includes(c.operator)
      ? { property: c.property, operator: c.operator }
      : { property: c.property, operator: c.operator || 'eq', value: c.value == null ? '' : String(c.value) }));
  return out.length ? { conditions: out } : {};
}

function timingToApi(f) {
  switch (f.type) {
    case 'event': {
      const mode = f.mode === 'tod' ? 'time_of_day' : f.mode;
      const base = { event: f.event, mode, keyField: f.keyField || null, versionField: f.versionField || null, ...condsApi(f.conditions) };
      if (mode === 'after') return { ...base, offset: { value: Number(f.amount), unit: f.unit } };
      if (mode === 'time_of_day') return { ...base, dateField: f.dateField, daysBefore: Number(f.days), timeOfDay: f.time, latePolicy: f.late };
      return { ...base, dateField: f.dateField, offset: { value: Number(f.amount), unit: f.unit }, latePolicy: f.late };
    }
    case 'schedule':
      return { repeat: f.repeat, weekdays: f.weekdays, monthDay: f.repeat === 'monthly' ? f.monthDay : null, times: (f.times || []).filter(Boolean), startDate: f.startDate };
    case 'prayer':
      return { ...prayerTiming(f), ...shafaqFor(f) };
    case 'inactivity':
      return f.inMode === 'today'
        ? { mode: 'not_opened_by', notOpenedBy: f.byTime, idle: null, repeat: f.inRepeat === 'repeat' ? { maxTimes: Number(f.inMax) } : null }
        : { mode: 'idle_for', idle: { value: Number(f.inAmount), unit: f.inUnit }, notOpenedBy: null, repeat: f.inRepeat === 'repeat' ? { everyDays: Number(f.inEvery), maxTimes: Number(f.inMax) } : null };
    case 'first_open':
      return { day: Number(f.foDays), timeOfDay: f.foTime, onlyIfFirstOpenAfter: f.foAfter || null };
    case 'profile':
      return { attribute: f.attr, mode: f.pdMode === 'before' ? 'days_before' : 'on_day', daysBefore: Number(f.pdAmount), timeOfDay: f.pdTime, yearly: !!f.yearly };
    case 'milestone':
      return { count: f.mCount === 'opens' ? 'app_opens' : 'event', event: f.mCount === 'event' ? f.mEvent : null, n: Number(f.mN), repeat: f.mRepeat, delay: { value: Number(f.mDelay), unit: f.mDelayUnit }, ...(f.mCount === 'event' ? condsApi(f.conditions) : {}) };
    default: return {};
  }
}

export function formToApi(f) {
  return {
    name: f.name, type: apiType(f.type),
    content: { title: f.title, body: f.body, tap: f.tap === 'link' ? { type: 'deep_link', uri: f.deeplink } : { type: 'app_home' }, variables: varsToApi(f.vars) },
    timing: timingToApi(f),
    limits: { quietHours: f.quiet ? { start: f.qStart, end: f.qEnd } : null, maxPerDay: f.maxPerDay === '' || f.maxPerDay == null ? null : Number(f.maxPerDay), priority: f.priority },
    stop: { cancelEvents: f.cancelEvents || [], endDate: f.endDate || null },
    audience: { mode: f.audMode, segments: f.segments || [], tags: f.tags || [] },
    // Optional conversion goal (D10); null clears it. Always sent: the form holds the stored goal.
    goal: goalToApi(f.goal),
  };
}

function timingToForm(f, t) {
  switch (f.type) {
    case 'event':
      f.event = t.event || ''; f.mode = t.mode === 'time_of_day' ? 'tod' : t.mode; f.keyField = t.keyField || ''; f.versionField = t.versionField || ''; f.dateField = t.dateField || ''; f.conditions = Array.isArray(t.conditions) ? t.conditions : [];
      if (t.offset) { f.amount = String(t.offset.value); f.unit = t.offset.unit; }
      if (t.daysBefore != null) f.days = String(t.daysBefore); if (t.timeOfDay) f.time = t.timeOfDay; if (t.latePolicy) f.late = t.latePolicy; break;
    case 'schedule':
      f.repeat = t.repeat; f.weekdays = t.weekdays || []; f.monthDay = t.monthDay == null ? '5' : String(t.monthDay); f.times = t.times || ['09:00']; f.startDate = t.startDate || ''; break;
    case 'prayer':
      f.pMode = t.mode === 'islamic_dates' ? 'islamic' : 'prayers'; f.prayers = t.prayers || []; f.islamic = t.islamicDates || []; f.pWhen = t.offsetMinutes ? 'before' : 'at'; f.pOffset = String(t.offsetMinutes || 10); f.method = t.method || 'karachi'; f.shafaq = t.shafaq || 'general'; f.asr = t.asr || 'standard'; f.locSource = t.location === 'profile_city' ? 'profile' : 'phone'; f.hijriAdj = String(t.hijriAdjustDays || 0); break;
    case 'inactivity':
      f.inMode = t.mode === 'not_opened_by' ? 'today' : 'days'; if (t.idle) { f.inAmount = String(t.idle.value); f.inUnit = t.idle.unit; } f.byTime = t.notOpenedBy || '20:00'; f.inRepeat = t.repeat ? 'repeat' : 'once'; if (t.repeat) { f.inEvery = String(t.repeat.everyDays || 7); f.inMax = String(t.repeat.maxTimes || 3); } break;
    case 'first_open':
      f.foDays = String(t.day); f.foTime = t.timeOfDay; f.foAfter = t.onlyIfFirstOpenAfter || ''; break;
    case 'profile':
      f.attr = t.attribute; f.pdMode = t.mode === 'days_before' ? 'before' : 'on'; f.pdAmount = String(t.daysBefore || 0); f.pdTime = t.timeOfDay; f.yearly = !!t.yearly; break;
    case 'milestone':
      f.mCount = t.count === 'app_opens' ? 'opens' : 'event'; f.mEvent = t.event || ''; f.mN = String(t.n); f.mRepeat = t.repeat; if (t.delay) { f.mDelay = String(t.delay.value); f.mDelayUnit = t.delay.unit; } f.conditions = Array.isArray(t.conditions) ? t.conditions : []; break;
    default: break;
  }
}

export function apiToForm(r) {
  const f = blankRule(flatType(r.type));
  f.id = r.id; f.name = r.name || ''; f.status = r.status;
  const c = r.content || {};
  f.title = c.title || ''; f.body = c.body || '';
  f.tap = c.tap && c.tap.type === 'deep_link' ? 'link' : 'home'; f.deeplink = c.tap && c.tap.uri ? c.tap.uri : '';
  f.vars = varsToForm(c.variables);
  const l = r.limits || {}; f.quiet = !!l.quietHours; if (l.quietHours) { f.qStart = l.quietHours.start; f.qEnd = l.quietHours.end; } f.maxPerDay = l.maxPerDay == null ? '' : String(l.maxPerDay); f.priority = l.priority || f.priority;
  const s = r.stop || {}; f.cancelEvents = s.cancelEvents || []; f.endDate = s.endDate || '';
  const a = r.audience || {}; f.audMode = a.mode || 'all'; f.segments = a.segments || []; f.tags = a.tags || [];
  f.goal = goalFormFromApi(r.goal);
  timingToForm(f, r.timing || {});
  return f;
}
